import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Edit2, Trash2 } from 'lucide-react';
import { Card, Badge, ConfirmationModal, toast } from '../components/v2/V2Components';
import InventoryService from '../services/InventoryService';
import { getItemImageUrl } from '../constants/images';
import ItemImageGallery from '../components/v2/AddAsset/ItemImageGallery';
import usePageView from '../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../constants/analyticsEvents';
import type { Item, Room } from '../types/item';
import { getCategoryIcon, formatCategory, getItemValue, getItemValueNum } from './inventory/itemHelpers';

/**
 * Dedicated item detail page mounted at /inventory/:id.
 *
 * Unlike the inventory list — whose light `GET /items` payload omits the full
 * images array, documents, and tasks — this page fetches the complete record
 * via `InventoryService.getItem(id)` on mount, so secondary/tag images and the
 * full specification blob render correctly. Navigation mirrors the app-wide
 * list→detail→edit pattern (see RoomDetails / /room/:id): Back returns to the
 * list, Edit routes to /inventory/edit/:id, and Delete returns to the list.
 */
const ItemDetails = () => {
    usePageView(ANALYTICS_EVENTS.INVENTORY_ITEM_DETAILS_VIEWED);
    const { id } = useParams();
    const navigate = useNavigate();

    const [item, setItem] = useState<Item | null>(null);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Delete confirmation
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const itemId = parseInt(id ?? '', 10);
        if (isNaN(itemId) || itemId <= 0) {
            setError('Invalid item ID');
            setLoading(false);
            return;
        }
        let cancelled = false;
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const itemData = await InventoryService.getItem(itemId);
                if (cancelled) return;
                setItem(itemData);
            } catch (err) {
                if (cancelled) return;
                console.error('Failed to load item:', err);
                setError('Failed to load item');
                return;
            } finally {
                if (!cancelled) setLoading(false);
            }
            // Rooms only supply the supplementary room-name label — a rooms
            // failure must not block the item from rendering (getRoomName
            // falls back to "Unknown"). Fetched separately for that reason.
            try {
                const roomsData = await InventoryService.getRooms();
                if (!cancelled) setRooms(Array.isArray(roomsData) ? roomsData : []);
            } catch (roomsErr) {
                console.error('Failed to load rooms:', roomsErr);
            }
        };
        loadData();
        return () => { cancelled = true; };
    }, [id]);

    const getRoomName = (roomId?: number | null): string => {
        const room = rooms.find(r => r.id === roomId);
        return room?.name || 'Unknown';
    };

    // Re-fetch the item after image-gallery actions so newly uploaded/deleted
    // images (and primary changes) reflect without a full page reload. Keyed
    // on the route `id` (not the whole `item`) so the callback identity stays
    // stable across item updates and doesn't churn ItemImageGallery's onChange.
    const refreshItem = useCallback(async () => {
        const itemId = parseInt(id ?? '', 10);
        if (isNaN(itemId) || itemId <= 0) return;
        try {
            const fresh = await InventoryService.getItem(itemId);
            setItem(fresh);
        } catch (err) {
            console.error('Failed to refresh item:', err);
        }
    }, [id]);

    const handleDelete = async () => {
        if (!item) return;
        try {
            setDeleting(true);
            await InventoryService.deleteItem(item.id);
            toast.success('Item deleted successfully');
            navigate('/inventory');
        } catch (err) {
            console.error('Failed to delete item:', err);
            toast.error('Failed to delete item');
            setDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="space-y-6 animate-fade-in">
                <button
                    onClick={() => navigate('/inventory')}
                    className="flex items-center text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors text-sm font-medium mb-4"
                >
                    <ChevronLeft size={16} className="mr-1" />
                    Back to Inventory
                </button>
                <Card className="p-8 text-center">
                    <p className="text-[var(--text-secondary)]">{error || 'Item not found.'}</p>
                </Card>
            </div>
        );
    }

    const Icon = getCategoryIcon(item.item_category || item.category);
    const imageUrl = getItemImageUrl(item);

    return (
        <div className="space-y-6 animate-fade-in">
            <button
                onClick={() => navigate('/inventory')}
                className="flex items-center text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors text-sm font-medium mb-4"
            >
                <ChevronLeft size={16} className="mr-1" />
                Back to Inventory
            </button>

            <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-headline font-extrabold tracking-tight text-[var(--primary)]">{item.name}</h1>
                        <Badge label={formatCategory(item.item_category || item.category)} variant="Pending" />
                    </div>
                    <p className="text-[var(--text-secondary)]">
                        {getRoomName(item.room_id)}
                        {item.installation_date && ` · Installed ${item.installation_date}`}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/inventory/edit/${item.id}`)}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-xl hover:bg-[var(--bg-elevated)] transition"
                    >
                        <Edit2 size={16} />
                        <span>Edit</span>
                    </button>
                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--error-container)] text-[var(--error)] rounded-xl hover:opacity-90 transition"
                    >
                        <Trash2 size={16} />
                        <span>Delete</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="lg:col-span-1 space-y-6">
                    <Card className="p-0 overflow-hidden">
                        <div className="h-48 bg-[var(--bg-hover)] flex items-center justify-center text-[var(--text-muted)]">
                            {imageUrl ? (
                                <img src={imageUrl} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                                <Icon size={64} />
                            )}
                        </div>
                        <div className="p-5 space-y-4">
                            {item.manufacturer && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Manufacturer</p>
                                    <p className="text-[var(--text-main)]">{item.manufacturer}</p>
                                </div>
                            )}
                            {(item.model_number || item.model) && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Model Number</p>
                                    <p className="text-[var(--text-main)] font-mono">{item.model_number || item.model}</p>
                                </div>
                            )}
                            {(item.serial_number || item.serial) && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Serial Number</p>
                                    <p className="text-[var(--text-main)] font-mono">{item.serial_number || item.serial}</p>
                                </div>
                            )}
                            {getItemValue(item) != null && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Value</p>
                                    <p className="text-[var(--text-main)] font-mono">
                                        ${getItemValueNum(item).toLocaleString()}
                                    </p>
                                </div>
                            )}
                            {item.notes && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Notes</p>
                                    <p className="text-sm text-[var(--text-main)] mt-1">{item.notes}</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>

                {/* Right Column */}
                <div className="lg:col-span-2 space-y-6">
                    <Card className="p-6">
                        <h3 className="font-headline font-bold text-[var(--text-main)] mb-4">Item Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Category</p>
                                <p className="text-[var(--text-main)]">{formatCategory(item.item_category || item.category)}</p>
                            </div>
                            {item.item_subcategory && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Subcategory</p>
                                    <p className="text-[var(--text-main)]">{item.item_subcategory}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Location</p>
                                <p className="text-[var(--text-main)]">{getRoomName(item.room_id)}</p>
                            </div>
                            {(item.purchase_date || item.date) && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Purchase Date</p>
                                    <p className="text-[var(--text-main)]">{item.purchase_date || item.date}</p>
                                </div>
                            )}
                            {item.warranty_expiration && (
                                <div>
                                    <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">Warranty Expires</p>
                                    <p className="text-[var(--text-main)]">{item.warranty_expiration}</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Images & Documents */}
                    <Card className="p-6">
                        <h3 className="font-headline font-bold text-[var(--text-main)] mb-4">Images &amp; Documents</h3>
                        <ItemImageGallery
                            itemId={item.id}
                            images={item.images || []}
                            onChange={refreshItem}
                        />
                    </Card>

                    {/* Specifications — only render values that are actually present */}
                    {(() => {
                        const specEntries = Object.entries(item.properties || {}).filter(([, value]) => {
                            if (value === null || value === undefined) return false;
                            if (typeof value === 'string') return value.trim() !== '';
                            if (Array.isArray(value)) return value.length > 0;
                            if (typeof value === 'object') return Object.keys(value).length > 0;
                            return true;
                        });
                        if (specEntries.length === 0) return null;
                        return (
                        <Card className="p-6">
                            <h3 className="font-headline font-bold text-[var(--text-main)] mb-4">Specifications</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {specEntries.map(([key, value]) => {
                                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                    if (typeof value === 'object' && value !== null) {
                                        return (
                                            <div key={key} className="col-span-2">
                                                <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">{label}</p>
                                                <pre className="text-xs text-[var(--text-main)] mt-1 bg-[var(--bg-hover)] p-2 rounded-lg overflow-auto">
                                                    {JSON.stringify(value, null, 2)}
                                                </pre>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={key}>
                                            <p className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider">{label}</p>
                                            <p className="text-[var(--text-main)]">{String(value)}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                        );
                    })()}

                    {/* Maintenance History */}
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-headline font-bold text-[var(--text-main)]">Maintenance History</h3>
                            <button className="text-sm text-[var(--primary)] font-semibold hover:underline">
                                Add Record
                            </button>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm">
                            No maintenance records yet.
                        </p>
                    </Card>
                </div>
            </div>

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Asset"
                message={`Are you sure you want to delete "${item.name}"? This action cannot be undone.`}
                confirmText="Delete"
                isDanger
                isLoading={deleting}
            />
        </div>
    );
};

export default ItemDetails;
